import { inventoryItemIdFormat } from "@redotech/redo-model/ids";
import { ReturnStatusZod } from "@redotech/redo-model/return-status";
import { sortingServiceLevel } from "@redotech/redo-model/returns/sorting-service-level";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";

// Return processing flow step schemas
export const ReturnProcessingStepScope = {
  RETURN: "return",
  RETURN_ITEM: "return_item",
} as const;
export type ReturnProcessingStepScope =
  (typeof ReturnProcessingStepScope)[keyof typeof ReturnProcessingStepScope];
const returnProcessingStepScopeValues = Object.values(
  ReturnProcessingStepScope,
) as [ReturnProcessingStepScope, ...ReturnProcessingStepScope[]];

export const ReturnProcessingStepType = {
  DECISION: "return_processing_decision",
  INFORMATION: "return_processing_information",
  LICENSE_PLATE: "license_plate",
  SORT: "sort",
  EVIDENCE: "evidence",
  ADD_UNDECLARED_ITEM: "add_undeclared_item",
} as const;
export type ReturnProcessingStepType =
  (typeof ReturnProcessingStepType)[keyof typeof ReturnProcessingStepType];

export const FlowActionType = {
  RESTOCK: "restock",
  MARK_MISSING: "mark_missing",
  FLAG_RETURN: "flag_return",
} as const;
export type FlowActionType =
  (typeof FlowActionType)[keyof typeof FlowActionType];

export const FlagReturnReason = ["damaged_item", "undeclared_item"] as const;

const RestockAction_ = z.object({ type: z.literal("restock") });
export const RestockAction: z.ZodType<RestockAction> = RestockAction_;
export interface RestockAction extends z.infer<typeof RestockAction_> {}

const MarkMissingAction_ = z.object({ type: z.literal("mark_missing") });
export const MarkMissingAction: z.ZodType<MarkMissingAction> =
  MarkMissingAction_;
export interface MarkMissingAction extends z.infer<typeof MarkMissingAction_> {}

const FlagReturnAction_ = z.object({
  type: z.literal("flag_return"),
  reason: z.enum(FlagReturnReason),
});
export const FlagReturnAction: z.ZodType<FlagReturnAction> = FlagReturnAction_;
export interface FlagReturnAction extends z.infer<typeof FlagReturnAction_> {}

const FlowAction_ = z.discriminatedUnion("type", [
  RestockAction_,
  MarkMissingAction_,
  FlagReturnAction_,
]);
export const FlowAction: z.ZodType<FlowAction> = FlowAction_;
export type FlowAction = z.infer<typeof FlowAction_>;

const ReturnProcessingDecisionStep_ = z.object({
  type: z.literal("return_processing_decision"),
  prompt: z.string(),
  decisionKey: z.string(),
  trueLabel: z.string(),
  falseLabel: z.string(),
  nextTrueId: z.number().nullable(),
  nextFalseId: z.number().nullable(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
  onTrueActions: z.array(FlowAction_).optional(),
  onFalseActions: z.array(FlowAction_).optional(),
});
export const ReturnProcessingDecisionStep: z.ZodType<ReturnProcessingDecisionStep> =
  ReturnProcessingDecisionStep_;
export interface ReturnProcessingDecisionStep
  extends z.infer<typeof ReturnProcessingDecisionStep_> {}

const ReturnProcessingInformationStep_ = z.object({
  type: z.literal("return_processing_information"),
  header: z.string(),
  message: z.string(),
  continueButtonText: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const ReturnProcessingInformationStep: z.ZodType<ReturnProcessingInformationStep> =
  ReturnProcessingInformationStep_;
export interface ReturnProcessingInformationStep
  extends z.infer<typeof ReturnProcessingInformationStep_> {}

const LicensePlateStep_ = z.object({
  type: z.literal("license_plate"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const LicensePlateStep: z.ZodType<LicensePlateStep> = LicensePlateStep_;
export interface LicensePlateStep extends z.infer<typeof LicensePlateStep_> {}

const SortStep_ = z.object({
  type: z.literal("sort"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  destination: z.string(),
  customTitle: z.string().optional(),
});
export const SortStep: z.ZodType<SortStep> = SortStep_;
export interface SortStep extends z.infer<typeof SortStep_> {}

const EvidenceStep_ = z.object({
  type: z.literal("evidence"),
  prompt: z.string(),
  notesLabel: z.string().optional(),
  notesPlaceholder: z.string().optional(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const EvidenceStep: z.ZodType<EvidenceStep> = EvidenceStep_;
export interface EvidenceStep extends z.infer<typeof EvidenceStep_> {}

const AddUndeclaredItemStep_ = z.object({
  type: z.literal("add_undeclared_item"),
  prompt: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const AddUndeclaredItemStep: z.ZodType<AddUndeclaredItemStep> =
  AddUndeclaredItemStep_;
export interface AddUndeclaredItemStep
  extends z.infer<typeof AddUndeclaredItemStep_> {}

const ReturnProcessingStep_ = z.discriminatedUnion("type", [
  ReturnProcessingDecisionStep_,
  ReturnProcessingInformationStep_,
  LicensePlateStep_,
  SortStep_,
  EvidenceStep_,
  AddUndeclaredItemStep_,
]);
export const ReturnProcessingStep: z.ZodType<ReturnProcessingStep> =
  ReturnProcessingStep_;
export type ReturnProcessingStep = z.infer<typeof ReturnProcessingStep_>;

const ReturnProcessingFlow_ = z.object({
  steps: z.array(ReturnProcessingStep_),
});
export const ReturnProcessingFlow: z.ZodType<ReturnProcessingFlow> =
  ReturnProcessingFlow_;
export interface ReturnProcessingFlow
  extends z.infer<typeof ReturnProcessingFlow_> {}

const ProcessingReturnItem_ = z.object({
  id: zExt.objectId(),
  productTitle: z.string(),
  variantTitle: z.string(),
  sku: z.string().nullish(),
  barcode: z.string().nullish(),
  quantity: z.number().int().positive(),
  images: z.array(z.string().nullable()),
  reason: z.string().nullish(),
  price: z.string(),
  currencyCode: z.string().nullish(),
});
export const ProcessingReturnItem: z.ZodType<ProcessingReturnItem> =
  ProcessingReturnItem_;
export interface ProcessingReturnItem
  extends z.infer<typeof ProcessingReturnItem_> {}

const DispositionSettings_ = z.object({
  enabled: z.boolean(),
  gradeOptions: z.array(z.string()),
  outcomeOptions: z.array(z.string()),
});
export const DispositionSettings: z.ZodType<DispositionSettings> =
  DispositionSettings_;
export interface DispositionSettings
  extends z.infer<typeof DispositionSettings_> {}

const SortingServiceLevelSchema_ = z.enum(sortingServiceLevel);
export const SortingServiceLevelSchema: z.ZodType<SortingServiceLevelSchema> =
  SortingServiceLevelSchema_;
export type SortingServiceLevelSchema = z.infer<
  typeof SortingServiceLevelSchema_
>;

const SortingInfo_ = z.object({
  serviceLevel: SortingServiceLevelSchema_,
  destination: z.string(),
  sortingFlow: ReturnProcessingFlow_,
});
export const SortingInfo: z.ZodType<SortingInfo> = SortingInfo_;
export interface SortingInfo extends z.infer<typeof SortingInfo_> {}

const ProcessingReturnData_ = z.object({
  id: zExt.objectId(),
  orderNumber: z.string(),
  status: ReturnStatusZod,
  items: z.array(ProcessingReturnItem_),
  dispositionSettings: DispositionSettings_.nullable(),
  returnProcessingFlow: ReturnProcessingFlow_,
  sortingInfo: SortingInfo_,
  createdAt: z.date(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
});
export const ProcessingReturnData: z.ZodType<ProcessingReturnData> =
  ProcessingReturnData_;
export interface ProcessingReturnData
  extends z.infer<typeof ProcessingReturnData_> {}

export const getReturnForProcessing = {
  name: "getReturnForProcessing" as const,
  input: z.object({ identifier: z.string().min(1) }),
  output: z.object({ return: ProcessingReturnData_.nullable() }),
};
